import React, { useState, useEffect } from 'react';
import {
  Edit3,
  Flame,
  LogIn,
  LogOut,
  Menu,
  User,
  ShieldAlert,
  Trophy,
  X,
} from 'lucide-react';
import { signOut } from '@/features/auth';
import { Button } from '@/components/ui/Button';
import { NotificationPopover } from '@/features/notifications';
import { ThemeToggle } from '@/features/theme';
import type { NotificationResourceType } from '@/types/database.types';

export type ActiveTab =
  | 'home'
  | 'feed'
  | 'templates'
  | 'projects'
  | 'editor'
  | 'profile'
  | 'health'
  | 'docs'
  | 'auth'
  | 'moderation'
  | 'contests';

export interface HeaderProps {
  activeTab: ActiveTab;
  onTabChange: (tab: ActiveTab, profileParams?: { userId?: string; username?: string }) => void;
  isAuthenticated?: boolean;
  isModerator?: boolean;
  currentUserId?: string | null;
  onSignIn?: () => void;
  onNavigateToNotificationResource?: (
    resourceType: NotificationResourceType,
    resourceId: string
  ) => void;
}

export const Header: React.FC<HeaderProps> = ({
  activeTab,
  onTabChange,
  isAuthenticated = true,
  isModerator = false,
  currentUserId = null,
  onSignIn,
  onNavigateToNotificationResource,
}) => {
  const [isSigningOut, setIsSigningOut] = useState(false);
  const [signOutError, setSignOutError] = useState<string | null>(null);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // Close mobile menu on Escape key press for accessibility
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isMobileMenuOpen) {
        setIsMobileMenuOpen(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isMobileMenuOpen]);

  useEffect(() => {
    if (!isAuthenticated || !currentUserId) {
      setIsSigningOut(false);
      setSignOutError(null);
    }
  }, [isAuthenticated, currentUserId]);

  const handleSignOut = async () => {
    setIsSigningOut(true);
    setSignOutError(null);

    try {
      const result = await signOut();
      if (result.error) {
        setSignOutError(result.error.message || 'Failed to sign out. Please try again.');
        setIsSigningOut(false);
      }
    } catch (error) {
      setSignOutError(error instanceof Error ? error.message : 'Failed to sign out. Please try again.');
      setIsSigningOut(false);
    }
  };

  const handleSelectTab = (tab: ActiveTab) => {
    onTabChange(tab);
    setIsMobileMenuOpen(false);
  };

  const navItems: { tab: ActiveTab; label: string; icon: React.ReactNode }[] = [
    { tab: 'home', label: 'Home', icon: <Flame size={16} /> },
    { tab: 'editor', label: 'Editor', icon: <Edit3 size={16} /> },
    { tab: 'feed', label: 'Community', icon: <Flame size={16} /> },
    { tab: 'contests', label: 'Contests', icon: <Trophy size={16} /> },
    { tab: 'profile', label: 'Profile', icon: <User size={16} /> },
    ...(isModerator ? [{ tab: 'moderation' as ActiveTab, label: 'Moderation', icon: <ShieldAlert size={16} /> }] : []),
  ];

  return (
    <header className="site-header">
      <div className="container">
        <div className="header-inner">
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <button
              onClick={() => handleSelectTab('home')}
              className="brand-logo"
              aria-label="Meme Mandi Home"
            >
              <div className="brand-mark">M</div>
              <span>Meme Mandi</span>
            </button>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '1.25rem' }}>
            {/* Desktop Navigation */}
            <nav className="desktop-nav" aria-label="Main Navigation">
              <ul className="nav-links">
                {navItems.map((item) => (
                  <li key={item.tab}>
                    <button
                      className={`nav-btn ${activeTab === item.tab ? 'active' : ''}`}
                      onClick={() => handleSelectTab(item.tab)}
                      aria-current={activeTab === item.tab ? 'page' : undefined}
                    >
                      {item.icon}
                      <span>{item.label}</span>
                    </button>
                  </li>
                ))}
              </ul>
            </nav>

            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              {signOutError && (
                <span
                  style={{
                    fontSize: '0.75rem',
                    color: 'var(--accent-danger)',
                    maxWidth: '12rem',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                  role="alert"
                  title={signOutError}
                >
                  {signOutError}
                </span>
              )}

              <ThemeToggle />

              {isAuthenticated && currentUserId && (
                <NotificationPopover
                  currentUserId={currentUserId}
                  onNavigateToResource={onNavigateToNotificationResource}
                />
              )}

              {isAuthenticated ? (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleSignOut}
                  disabled={isSigningOut}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '0.375rem',
                    opacity: isSigningOut ? 0.6 : 1,
                    cursor: isSigningOut ? 'not-allowed' : 'pointer',
                  }}
                  aria-label="Sign out"
                >
                  <LogOut size={14} />
                  <span>{isSigningOut ? 'Signing out...' : 'Sign out'}</span>
                </Button>
              ) : (
                <Button
                  variant="primary"
                  size="sm"
                  onClick={onSignIn || (() => handleSelectTab('auth'))}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '0.375rem',
                  }}
                  aria-label="Sign in"
                >
                  <LogIn size={14} />
                  <span>Sign in</span>
                </Button>
              )}

              {/* Mobile Menu Toggle Button */}
              <button
                className="mobile-menu-btn"
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                aria-expanded={isMobileMenuOpen}
                aria-controls="mobile-nav-drawer"
                aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
              >
                {isMobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Navigation Drawer */}
        {isMobileMenuOpen && (
          <nav
            id="mobile-nav-drawer"
            className="mobile-nav-drawer"
            aria-label="Mobile Navigation"
          >
            <ul className="mobile-nav-list">
              {navItems.map((item) => (
                <li key={item.tab}>
                  <button
                    className={`mobile-nav-btn ${activeTab === item.tab ? 'active' : ''}`}
                    onClick={() => handleSelectTab(item.tab)}
                    aria-current={activeTab === item.tab ? 'page' : undefined}
                  >
                    {item.icon}
                    <span>{item.label}</span>
                  </button>
                </li>
              ))}
              <li style={{ padding: '0.5rem 1rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderTop: '1px solid var(--border-subtle)', marginTop: '0.25rem' }}>
                <span style={{ fontSize: '0.86rem', fontWeight: 650, color: 'var(--text-secondary)' }}>Theme</span>
                <ThemeToggle />
              </li>
            </ul>
          </nav>
        )}
      </div>
    </header>
  );
};
